import React, { useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import styledImport from 'styled-components';
import type { ThemedStyledInterface } from 'styled-components';

import { Breakpoints, Color, Grid, Transition, Type } from '../../../app/StyleGuide';
import type { NavbarTheme } from '../state/Navbar.theme';

const styled = styledImport as ThemedStyledInterface<NavbarTheme>;

const MoreMenuRoot = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 100%;
`;

const MoreMenuTrigger = styled.button<{ $active?: boolean }>`
  appearance: none;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: 100%;
  height: 60px;
  margin: 0;
  border: 0;
  padding: 16px;
  background: transparent;
  font: inherit;
  text-align: left;
  cursor: pointer;
  line-height: 1.4;
  color: inherit;
  text-transform: ${({ theme }) => theme.navTextTransform};
  letter-spacing: ${({ theme }) => theme.navLetterSpacing};
  font-size: ${({ theme }) => theme.navFontSize};
  font-weight: ${({ theme }) => theme.navFontWeight};
  white-space: nowrap;

  &:hover,
  &:focus {
    color: inherit;
    outline: none;
  }

  i {
    margin-right: 5px;
    color: ${({ $active, theme }) => ($active ? theme.activeIconColor : theme.navIconColor)};
    opacity: ${({ $active, theme }) => ($active ? theme.activeIconOpacity : theme.navIconOpacity)};
    font-size: 16px;
    transition: color 0.125s linear, opacity 0.125s linear;
  }
`;

const HiddenSmallText = styled.span`
  display: none;

  @media (min-width: ${Breakpoints.screen_md}) {
    display: inline;
  }
`;

const MoreMenuItem = styled.li<{ $active: boolean }>`
  display: flex;
  flex: 0 0 auto;
  justify-content: center;
  background: ${({ $active, theme }) => ($active ? theme.activeBackground : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.activeTextColor : theme.textColor)};
  transition: background-color 0.125s linear, color 0.125s linear;

  &:hover {
    background: ${({ $active, theme }) => ($active ? theme.activeBackground : theme.hoverBackground)};
  }
`;

const MenuPanel = styled.div`
  position: fixed;
  transform: translateX(-100%);
  background: ${Color.white};
  border: 1px solid ${Color.Gray._20};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 250px;
  z-index: 1100;
  padding: ${Grid._2};
`;

const MenuRow = styled.button`
  appearance: none;
  width: 100%;
  margin: 0;
  border: none;
  padding: ${Grid._4} ${Grid._5};
  cursor: pointer;
  transition: ${Transition.fast};
  background: transparent;
  color: ${Type.Color.dark};
  font-size: ${Type.Scale._2};
  font-family: inherit;
  text-align: left;
  display: flex;
  align-items: center;
  gap: ${Grid._2};

  &:hover {
    background: ${Color.Gray._05};
  }

  i {
    margin-right: ${Grid._2};
  }
`;

type NavbarMoreMenuProps = {
  actions: {
    key: string;
    label: string;
    iconClass: string;
    qaHook: string;
    active?: boolean;
    onSelect: () => void;
  }[];
};

export default function NavbarMoreMenu({ actions }: NavbarMoreMenuProps) {
  const location = useLocation();
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const updatePosition = () => {
      const el = rootRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom, left: rect.right });
    };

    updatePosition();

    const onResizeOrScroll = () => updatePosition();
    window.addEventListener('resize', onResizeOrScroll);
    window.addEventListener('scroll', onResizeOrScroll, true);

    const onDocMouseDown = (event: MouseEvent) => {
      const node = event.target as Node;
      if (rootRef.current?.contains(node) || menuRef.current?.contains(node)) return;
      setIsOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('resize', onResizeOrScroll);
      window.removeEventListener('scroll', onResizeOrScroll, true);
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const toggle = () => {
    setIsOpen(prev => !prev);
  };

  return (
    <MoreMenuItem $active={false}>
      <MoreMenuRoot ref={rootRef}>
        <MoreMenuTrigger
          type="button"
          data-qa-hook="dropdown"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          aria-controls={menuId}
          id={`${menuId}-trigger`}
          onClick={toggle}
        >
          <i className="fas fa-ellipsis-v" aria-hidden /> <HiddenSmallText>More</HiddenSmallText>
        </MoreMenuTrigger>
      </MoreMenuRoot>
      {isOpen &&
        createPortal(
          <MenuPanel
            ref={menuRef}
            id={menuId}
            role="menu"
            data-qa-hook="dropdownView"
            aria-labelledby={`${menuId}-trigger`}
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
            }}
          >
            {actions.map(action => (
              <MenuRow
                key={action.key}
                type="button"
                role="menuitem"
                data-qa-hook={action.qaHook}
                onClick={() => {
                  setIsOpen(false);
                  action.onSelect();
                }}
              >
                <i className={action.iconClass} aria-hidden /> {action.label}
              </MenuRow>
            ))}
          </MenuPanel>,
          document.body
        )}
    </MoreMenuItem>
  );
}
